import React, { useState, useEffect } from "react";
import { authAxios, API, useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Smartphone, MessageSquare, Bot, Users, Inbox, Package, ShoppingCart, Zap, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const statusColors = {
  connected: "bg-[#22c55e] text-white",
  not_connected: "bg-slate-200 text-slate-600",
  disconnected: "bg-red-100 text-red-600",
  generating_qr: "bg-yellow-100 text-yellow-700",
  qr_ready: "bg-blue-100 text-blue-700",
  authenticated: "bg-[#22c55e]/20 text-[#22c55e]",
};

export default function DashboardHome() {
  const { user } = useAuth();
  const { branding } = useBranding();
  const [summary, setSummary] = useState(null);
  const [salesSummary, setSalesSummary] = useState(null);
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const [s, ss, sub] = await Promise.all([
          authAxios.get(`${API}/dashboard/summary`),
          authAxios.get(`${API}/sales/summary`).catch(() => ({ data: null })),
          authAxios.get(`${API}/me/subscription-status`).catch(() => ({ data: null })),
        ]);
        setSummary(s.data);
        setSalesSummary(ss.data);
        setSubStatus(sub.data);
      } catch (err) {
        console.error("Failed to fetch summary:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-slate-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const waStatus = summary?.whatsapp_status || "not_connected";

  return (
    <div className="p-8" data-testid="dashboard-home">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold" style={{ color: branding.primary_color || "#0f172a" }}>
          Welcome back, {user?.name || "User"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {branding.dashboard_welcome_text || "Here's an overview of your WhatsApp Bot workspace"}
        </p>
      </div>

      {/* Plan banner — pending request OR no active plan */}
      {(!subStatus?.approved || subStatus?.pending) && (
        <div className="mb-6 rounded-2xl p-5 flex items-center gap-4"
             style={{ background: "linear-gradient(135deg,#fffbeb 0%,#fef3c7 100%)", border: "1px solid #fcd34d" }}
             data-testid="plan-banner">
          <div className="w-11 h-11 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            {subStatus?.pending ? (
              <>
                <p className="font-semibold text-amber-900">Solicitud en revisión</p>
                <p className="text-sm text-amber-800">
                  Tu plan <strong>{subStatus.pending.plan_name}</strong> ({subStatus.pending.payment_method}) está pendiente de aprobación. Apenas confirmemos el pago, te activamos todas las funciones.
                </p>
              </>
            ) : (
              <>
                <p className="font-semibold text-amber-900">Activá un plan para usar el bot</p>
                <p className="text-sm text-amber-800">Tu cuenta está creada pero las funciones del bot están bloqueadas hasta confirmar un plan.</p>
              </>
            )}
          </div>
          <Link to="/dashboard/plans" className="shrink-0 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium transition" data-testid="plan-banner-cta">
            {subStatus?.pending ? "Ver estado" : "Elegir plan"}
          </Link>
        </div>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Link to="/dashboard/connect-whatsapp">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" data-testid="card-whatsapp-status">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Smartphone className="w-5 h-5 text-muted-foreground" />
                <Badge className={statusColors[waStatus] || statusColors.not_connected}>
                  {waStatus.replace(/_/g, " ")}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm font-medium">WhatsApp Connection</p>
              <p className="text-xs text-muted-foreground mt-1">
                {summary?.whatsapp_phone || "No phone connected"}
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/inbox">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" data-testid="card-inbox">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Inbox className="w-5 h-5 text-muted-foreground" />
                <span className="text-2xl font-bold text-[#0f172a]">{summary?.conversation_count || 0}</span>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm font-medium">Conversations</p>
              <p className="text-xs text-muted-foreground mt-1">
                {summary?.message_count || 0} total messages
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/bot-settings">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" data-testid="card-bot-settings">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Bot className="w-5 h-5 text-muted-foreground" />
                <Badge className={summary?.bot_enabled ? "bg-[#22c55e] text-white" : "bg-slate-200 text-slate-600"}>
                  {summary?.bot_enabled ? "Active" : "Inactive"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm font-medium">Private Auto-Reply</p>
              <p className="text-xs text-muted-foreground mt-1">Auto-respond to private messages</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/group-settings">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" data-testid="card-group-settings">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Users className="w-5 h-5 text-muted-foreground" />
                <Badge className={summary?.group_enabled ? "bg-[#22c55e] text-white" : "bg-slate-200 text-slate-600"}>
                  {summary?.group_enabled ? "Active" : "Inactive"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm font-medium">Group Replies</p>
              <p className="text-xs text-muted-foreground mt-1">Trigger-based group responses</p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Sales Summary */}
      {salesSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="sales-summary">
          <Link to="/dashboard/catalog">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-5">
                <Package className="w-5 h-5 text-muted-foreground mb-2" />
                <p className="text-2xl font-bold">{salesSummary.active_products}/{salesSummary.products}</p>
                <p className="text-xs text-muted-foreground">Productos activos</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/dashboard/orders">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-5">
                <ShoppingCart className="w-5 h-5 text-muted-foreground mb-2" />
                <p className="text-2xl font-bold">{salesSummary.pending_orders}</p>
                <p className="text-xs text-muted-foreground">Pedidos pendientes</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/dashboard/orders">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-5">
                <ShoppingCart className="w-5 h-5 text-emerald-600 mb-2" />
                <p className="text-2xl font-bold">{salesSummary.paid_orders}</p>
                <p className="text-xs text-muted-foreground">Pedidos pagados</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/dashboard/keywords">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-5">
                <Zap className="w-5 h-5 text-muted-foreground mb-2" />
                <p className="text-2xl font-bold">{salesSummary.keyword_triggers}</p>
                <p className="text-xs text-muted-foreground">Palabras clave activas</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      {/* Recent Conversations */}
      <Card data-testid="recent-conversations">
        <CardHeader>
          <CardTitle className="text-base">Recent Conversations</CardTitle>
        </CardHeader>
        <CardContent>
          {(!summary?.recent_conversations || summary.recent_conversations.length === 0) ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No conversations yet</p>
              <p className="text-xs mt-1">Connect your WhatsApp to start receiving messages</p>
            </div>
          ) : (
            <div className="space-y-3">
              {summary.recent_conversations.map((conv, i) => (
                <div key={conv.id || i} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      {conv.is_group ? <Users className="w-4 h-4 text-slate-500" /> : <MessageSquare className="w-4 h-4 text-slate-500" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{conv.name}</p>
                      <p className="text-xs text-muted-foreground truncate max-w-[200px]">{conv.last_message}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {conv.is_group ? "Group" : "Private"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
